import React, { useEffect } from 'react';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { useToggle } from 'react-use';
import {
  Box,
  Fab,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography,
} from '@material-ui/core';
import { Refresh as RefreshIcon } from '@material-ui/icons';
import { AppBar, Page, ScreenLoader, EmptyState, ConfirmationDialog, Snackbar } from 'components';
import { anketsStore, scoreStore, backNavigationStore } from 'stores';
import { ru } from 'locale';
import { NoData as NoDataBg } from 'resources/images';
import { formatDate, fullDateFormat } from 'utils/date';
import ScoreItem from './components/ScoreItem';
import useStyles from './ScoreScreen.styles';

const ScoreScreen = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const { score, fetchState, updateScoreState, forceUpdateScore } = scoreStore;
  const { backUrl } = backNavigationStore;
  const [isConfirmationDialogOpen, toggleConfirmationDialog] = useToggle(false);
  const [isPopupOpen, togglePopup] = useToggle(false);

  useEffect(
    () =>
      autorun(() => {
        scoreStore.getScore(anketsStore.anketId);
      }),
    [],
  );

  useEffect(() => {
    if (updateScoreState === 'fulfilled') {
      toggleConfirmationDialog();
    }
    if (updateScoreState === 'rejected') {
      togglePopup();
    }
  }, [toggleConfirmationDialog, togglePopup, updateScoreState]);

  const handleBackClick = () => {
    history.goBack();
  };

  const handleUpdateScore = () => {
    forceUpdateScore();
  };

  const renderMarkers = () => {
    if (score.groups.length === 0) {
      return (
        <Box my={2}>
          <EmptyState image={{ source: NoDataBg, alt: ru.markerList }} title={ru.notFoundMarkers} />
        </Box>
      );
    }

    return score.groups.map(group => (
      <Box my={2} key={group.title}>
        <ScoreItem category={group} />
      </Box>
    ));
  };

  const renderScoreInfo = () => {
    const lastUpdateDate = new Date(score.actualDate);
    const formattedDate = formatDate(lastUpdateDate, fullDateFormat);

    return (
      <Box px={2} py={2}>
        <Card className={classes.card}>
          <CardHeader title={ru.total} titleTypographyProps={{ variant: 'h6' }} />
          <Divider />
          <CardContent>
            <Typography variant="h2" color="primary">
              {score.value}
            </Typography>
            <Typography variant="body1" component="span" color="textSecondary">
              {`${ru.actualDateOfScore}: `}
            </Typography>
            <Typography variant="body1" color="textPrimary" component="span">
              {formattedDate}
            </Typography>
          </CardContent>
        </Card>

        {renderMarkers()}
      </Box>
    );
  };

  return (
    <Page>
      <Grid container direction="column">
        <Grid item>
          <AppBar
            title={ru.scoringScore}
            onNavButtonClick={handleBackClick}
            backUrl={`${backUrl}#score-info`}
            typeNavIcon="link"
          />
        </Grid>
        <Grid item>
          {fetchState === 'pending' && <ScreenLoader />}
          {fetchState === 'fulfilled' && renderScoreInfo()}
        </Grid>
      </Grid>

      <Fab
        onClick={handleUpdateScore}
        className={classes.updateScore}
        color="secondary"
        aria-label={ru.updateScore}
      >
        <RefreshIcon />
      </Fab>

      <Snackbar
        open={isPopupOpen}
        onClose={togglePopup}
        message={ru.couldNotUpdateScore}
        variant="error"
      />

      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        title={ru.updatingScore}
        description={ru.updatingScoreTakesTime}
        submitButtonText="Ok"
        onSubmit={toggleConfirmationDialog}
      />
    </Page>
  );
});

export default ScoreScreen;
